import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useApp } from "../../../../context/AppState";
import { parseCssStringToObject } from "../../../../helper";
import { formatStorefrontMoney } from "../../../../utils/format-storefront-money";
import { DEFAULT_BLOCK_CSS, TEMPLATE_TYPE } from "../../../../constant/constant";
import {
  isPopupLayout,
  POPUP_LAYOUT_IDS,
} from "../../../../popup-layouts/index.js";
import {
  VARIANT_PICKER_STYLE,
  getVariantPickerDisplay,
} from "../../../../../shared/product-block-display.js";
import { isExtensionWidgetPopup } from "../../../../../shared/extension-widget-display-defaults.js";
import { resolvePopupThemeTokens } from "../../../../utils/resolve-popup-theme-tokens.js";
import { isUpsellRecommendationProductPopup } from "../../../../../shared/product-upsell-recommendation-defaults.js";
import {
  productHasSelectableVariants,
  findVariantByFormValue,
  getProductVariants,
  getVariantFormValue,
  isVariantAvailable,
  withOutOfStockLabel,
} from "../../../../layouts/product-recommendation/utils/product-variant-utils.js";
import {
  getCustomCartDrawerLayout,
  isStaticCustomCartDrawer,
} from "../../../../constants/custom-cart-drawer-template";
import CartDrawerVariantSelect from "../../../CartDrawer/CartDrawerVariantSelect";

const PRODUCT_PAGE_PICKER_Z_INDEX = 2147483647;

function buildFixedMenuStyle(triggerEl, openDirectionUp) {
  if (!triggerEl || typeof window === "undefined") {
    return null;
  }

  const rect = triggerEl.getBoundingClientRect();
  const gap = 4;
  return {
    position: "fixed",
    left: rect.left,
    width: rect.width,
    zIndex: PRODUCT_PAGE_PICKER_Z_INDEX,
    ...(openDirectionUp
      ? { bottom: window.innerHeight - rect.top + gap }
      : { top: rect.bottom + gap }),
  };
}

function isPointerInsideVariantPicker(event, containerEl, menuEl) {
  const path =
    typeof event.composedPath === "function"
      ? event.composedPath()
      : [event.target];

  return path.some((node) => {
    if (!node || typeof node.getAttribute !== "function") {
      return false;
    }
    if (containerEl && node === containerEl) {
      return true;
    }
    if (menuEl && node === menuEl) {
      return true;
    }
    return (
      node.getAttribute("data-variant-picker-trigger") != null ||
      node.getAttribute("data-variant-picker-menu") != null
    );
  });
}

const VariantPickerBlock = ({ popupData, section, productIndex, productData }) => {
  const formContext = useFormContext();
  const control = formContext?.control;
  const setValue = formContext?.setValue;
  const hasFormContext = Boolean(formContext);
  const { isMobile, appState } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(productData?.variants?.[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const tabScrollRef = useRef(null);
  const [openDirectionUp, setOpenDirectionUp] = useState(false);
  const [fixedMenuStyle, setFixedMenuStyle] = useState(null);
  const { is_preview_mode } = appState;
  const name = `${section?.type}-${productIndex}`;
  const isNativeDropDown = popupData?.popUpType === TEMPLATE_TYPE.BOGO_BUNDLE;
  const isAiRecommendationUpsellLayout = isPopupLayout(
    popupData,
    POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL
  );
  const isProductRecommendationLayout = isPopupLayout(
    popupData,
    POPUP_LAYOUT_IDS.PRODUCT_RECOMMENDATION
  );
  const isProductPageUpsellLayout = isPopupLayout(
    popupData,
    POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL
  );
  const isRecommendationPopupTemplate = useMemo(() => {
    const templateTitle =
      popupData?.template_id?.title || popupData?.generalSettings?.title || "";
    return String(templateTitle).trim().toLowerCase() === "recommendation popup";
  }, [popupData?.template_id?.title, popupData?.generalSettings?.title]);
  const isVerticalRecommendationCard =
    isProductRecommendationLayout ||
    isRecommendationPopupTemplate ||
    isUpsellRecommendationProductPopup(popupData);
  const isProductPageUpsell =
    isProductPageUpsellLayout ||
    popupData?.generalSettings?.isStaticProductPageUpsell === true;
  const isCatalogCompactCard =
    isVerticalRecommendationCard || isProductPageUpsell;
  const isExtensionWidget = isExtensionWidgetPopup(popupData);
  const isStaticCartDrawer = isStaticCustomCartDrawer(popupData);
  const variantPickerDisplay = getVariantPickerDisplay(section);
  const pickerLabel = variantPickerDisplay.label;
  const variantPickerVariant = isExtensionWidget
    ? VARIANT_PICKER_STYLE.DROPDOWN
    : variantPickerDisplay.variantPickerVariant ??
      (isAiRecommendationUpsellLayout
        ? VARIANT_PICKER_STYLE.SLIDER
        : isCatalogCompactCard
          ? VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN
          : VARIANT_PICKER_STYLE.DROPDOWN);
  const isSliderVariant = variantPickerVariant === VARIANT_PICKER_STYLE.SLIDER;
  const isDropdownVariant = variantPickerVariant === VARIANT_PICKER_STYLE.DROPDOWN;
  const isPremiumDropdownVariant =
    variantPickerVariant === VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN;

  const themeTokens = useMemo(
    () =>
      isAiRecommendationUpsellLayout || isCatalogCompactCard
        ? resolvePopupThemeTokens(popupData, { activeStep: 0, isMobile })
        : null,
    [isAiRecommendationUpsellLayout, isCatalogCompactCard, popupData, isMobile]
  );

  const computeDropdownOpensUpward = () => {
    const cardEl =
      containerRef.current?.closest("article[data-shell-layout]") ||
      containerRef.current?.closest("article");
    const isHorizontalShell =
      cardEl?.getAttribute("data-shell-layout") === "cart-drawer-horizontal-v1";

    if (!dropdownRef.current || !containerRef.current) {
      return false;
    }

    const dropdownHeight =
      dropdownRef.current.getBoundingClientRect().height || 200;
    const containerRect = containerRef.current.getBoundingClientRect();

    if (isHorizontalShell) {
      const spaceBelowViewport = window.innerHeight - containerRect.bottom;
      if (isProductPageUpsell) {
        if (spaceBelowViewport < dropdownHeight + 12) return true;
        if (cardEl) {
          const cardRect = cardEl.getBoundingClientRect();
          const spaceBelowInCard = cardRect.bottom - containerRect.bottom;
          const spaceAboveInCard = containerRect.top - cardRect.top;
          if (spaceBelowInCard < dropdownHeight + 8) {
            return spaceAboveInCard >= dropdownHeight * 0.5 || spaceAboveInCard > spaceBelowInCard;
          }
        }
        return false;
      }

      if (spaceBelowViewport >= dropdownHeight + 8) return false;

      if (cardEl) {
        const cardRect = cardEl.getBoundingClientRect();
        const spaceBelowInCard = cardRect.bottom - containerRect.bottom;
        const spaceAboveInCard = containerRect.top - cardRect.top;
        if (spaceBelowInCard >= dropdownHeight + 8) return false;
        return (
          spaceAboveInCard >= dropdownHeight &&
          spaceAboveInCard > spaceBelowInCard
        );
      }

      return spaceBelowViewport < dropdownHeight;
    }

    if (isCatalogCompactCard) {
      const controlsEl = containerRef.current.closest("[data-catalog-controls]");
      const ctaEl = controlsEl?.querySelector("[data-catalog-cta]");

      if (ctaEl) {
        const spaceToCta = ctaEl.getBoundingClientRect().top - containerRect.bottom;
        if (spaceToCta < dropdownHeight + 4) {
          const cardRect = cardEl?.getBoundingClientRect();
          const spaceAbove = cardRect
            ? containerRect.top - cardRect.top
            : containerRect.top;
          return spaceAbove >= dropdownHeight || spaceAbove > spaceToCta;
        }
        return false;
      }

      if (controlsEl) {
        const controlsRect = controlsEl.getBoundingClientRect();
        const spaceBelow = controlsRect.bottom - containerRect.bottom - 44;
        if (spaceBelow < dropdownHeight) {
          const cardRect = cardEl?.getBoundingClientRect();
          const spaceAbove = cardRect
            ? containerRect.top - cardRect.top
            : containerRect.top;
          return spaceAbove >= dropdownHeight * 0.6;
        }
      }

      return false;
    }

    const spaceBelowViewport = window.innerHeight - containerRect.bottom;
    return spaceBelowViewport < dropdownHeight;
  };

  const catalogVariants = getProductVariants(productData);

  const resolvedVariants = useMemo(() => {
    if (catalogVariants.length > 0) {
      return catalogVariants;
    }
    if (!isCatalogCompactCard || !productData) {
      return [];
    }
    // Prefer a real Shopify variant id — synthetic "*-default-variant" ids are
    // rejected by Product Add-Ons cart prepend (coerceShopifyVariantId).
    const fallbackId =
      productData.variant_id ??
      productData.variantId ??
      productData.selected_or_first_available_variant?.id ??
      null;
    if (fallbackId == null || fallbackId === "") {
      return [];
    }
    return [
      {
        id: fallbackId,
        title: productData.title || "Default",
        price: productData.price ?? productData.variants?.[0]?.price ?? "",
      },
    ];
  }, [catalogVariants, isCatalogCompactCard, productData?.id, productData?.title, productData?.price, productData?.variant_id, productData?.variantId, productData?.selected_or_first_available_variant?.id]);

  const productIdentity = `${productData?.id ?? "product"}:${productIndex}`;
  const variantSignature = resolvedVariants.map(getVariantFormValue).join("|");
  const watchedVariantId = hasFormContext
    ? useWatch({ control, name, disabled: !hasFormContext })
    : undefined;

  useEffect(() => {
    if (!isOpen) {
      setOpenDirectionUp(false);
      return;
    }

    const updateDirection = () => {
      setOpenDirectionUp(computeDropdownOpensUpward());
    };

    updateDirection();
    const raf = requestAnimationFrame(updateDirection);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, isCatalogCompactCard, searchTerm, resolvedVariants.length]);

  const updateFixedMenuPosition = useCallback(() => {
    if (!isProductPageUpsell || !isOpen || !containerRef.current) {
      setFixedMenuStyle(null);
      return;
    }

    const triggerEl = containerRef.current.querySelector("[data-variant-picker-trigger]");
    if (!triggerEl) {
      setFixedMenuStyle(null);
      return;
    }

    setFixedMenuStyle(buildFixedMenuStyle(triggerEl, openDirectionUp));
  }, [isOpen, isProductPageUpsell, openDirectionUp]);

  useLayoutEffect(() => {
    if (!isProductPageUpsell || !isOpen) {
      setFixedMenuStyle(null);
      return undefined;
    }

    updateFixedMenuPosition();
    const raf = requestAnimationFrame(updateFixedMenuPosition);
    window.addEventListener("scroll", updateFixedMenuPosition, true);
    window.addEventListener("resize", updateFixedMenuPosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updateFixedMenuPosition, true);
      window.removeEventListener("resize", updateFixedMenuPosition);
    };
  }, [
    isOpen,
    isProductPageUpsell,
    openDirectionUp,
    resolvedVariants.length,
    searchTerm,
    updateFixedMenuPosition,
  ]);

  useEffect(() => {
    if (!isOpen || !isCatalogCompactCard) return undefined;

    const handlePointerDown = (event) => {
      if (
        isPointerInsideVariantPicker(
          event,
          containerRef.current,
          dropdownRef.current
        )
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, isCatalogCompactCard]);

  const isPickerDisabled =
    isCatalogCompactCard &&
    (catalogVariants.length <= 1 || !productHasSelectableVariants(productData));

  // Reset only when the product or variant list changes — not when the user picks a variant.
  useEffect(() => {
    const firstVariant = resolvedVariants[0];
    if (!firstVariant) {
      setSelected(undefined);
      return;
    }
    setSelected(firstVariant);
    if (!setValue) return;
    setValue(name, getVariantFormValue(firstVariant), {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [productIdentity, name, variantSignature, setValue]);

  useEffect(() => {
    if (!setValue || !resolvedVariants.length) return;
    if (watchedVariantId != null && watchedVariantId !== "") return;
    const firstVariant = resolvedVariants[0];
    setSelected(firstVariant);
    setValue(name, getVariantFormValue(firstVariant), {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [name, variantSignature, setValue, watchedVariantId]);

  useEffect(() => {
    if (watchedVariantId == null || watchedVariantId === "") return;
    const match = findVariantByFormValue(resolvedVariants, watchedVariantId);
    if (!match) return;
    setSelected((prev) =>
      getVariantFormValue(prev) === getVariantFormValue(match) ? prev : match
    );
  }, [watchedVariantId, variantSignature]);

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };

  /** Drop centering from DEFAULT_BLOCK_CSS so dropdown labels align to the start. */
  const pickerShellCSS = useMemo(() => {
    const { justifyContent, textAlign, alignItems, ...rest } = blockCSS;
    return rest;
  }, [blockCSS]);

  const GENERIC_VARIANT_TITLES = new Set(["default title", "default"]);

  const resolveVariantDisplayTitle = (variant) => {
    const variantTitle = String(variant?.title || "").trim();
    const productTitle = String(productData?.title || "").trim();
    if (
      !variantTitle ||
      GENERIC_VARIANT_TITLES.has(variantTitle.toLowerCase())
    ) {
      return productTitle || variantTitle || "Default";
    }
    return variantTitle;
  };

  const formatVariantLabel = (variant, { includeAvailability = true } = {}) => {
    const title = isCatalogCompactCard
      ? resolveVariantDisplayTitle(variant)
      : String(variant?.title || "").trim();
    const price = variant?.price;
    let label = title;
    if (price != null && price !== "" && price !== "XXX.XX") {
      const numeric = Number(price);
      label = Number.isFinite(numeric)
        ? `${title} | ${formatStorefrontMoney(numeric, appState)}`
        : `${title} | ${price}`;
    }
    return includeAvailability ? withOutOfStockLabel(label, variant) : label;
  };

  const handleSelectAvailable = (variant, onChange) => {
    if (!isVariantAvailable(variant)) return;
    handleSelect(variant, onChange);
  };

  const filteredVariants = useMemo(
    () =>
      resolvedVariants.filter((variant) =>
        String(variant?.title || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    [resolvedVariants, searchTerm]
  );

  const handleSelect = (variant, onChange) => {
    const value = getVariantFormValue(variant);
    setSelected(variant);
    setIsOpen(false);
    setSearchTerm("");
    onChange(value);
    if (setValue) {
      setValue(name, value, { shouldDirty: true, shouldValidate: false });
    }
  };

  const ArrowIcon = ({ open }) => (
    <svg
      fill={blockCSS?.color ?? "#334155"}
      width={blockCSS?.fontSize ? `${parseInt(blockCSS.fontSize, 10) - 4}` : "12px"}
      height={blockCSS?.fontSize ? `${parseInt(blockCSS.fontSize, 10) - 4}` : "12px"}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{ pointerEvents: "none" }}
    >
      {open ? (
        <path d="M11.178 19.569a.998.998 0 0 0 1.644 0l9-13A.999.999 0 0 0 21 5H3a1.002 1.002 0 0 0-.822 1.569l9 13z" />
      ) : (
        <path d="M11.178 4.431a.998.998 0 0 0-1.644 0l-9 13A.999.999 0 0 0 3 19h18a1.002 1.002 0 0 0 .822-1.569l-9-13z" />
      )}
    </svg>
  );

  const PremiumChevronIcon = ({ open, muted = false }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{
        pointerEvents: "none",
        flexShrink: 0,
        transform: open ? "rotate(180deg)" : "none",
        transition: "transform 0.15s ease",
      }}
    >
      <path
        d="M2.5 4.5L6 8L9.5 4.5"
        stroke={muted ? "#94a3b8" : "#334155"}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const applyVariantFieldChange = (field, nextValue) => {
    if (isPickerDisabled) return;
    const selectedVariant = resolvedVariants.find(
      (v) =>
        String(v.id) === String(nextValue) ||
        String(v.admin_graphql_api_id?.split("/")?.pop()) === String(nextValue)
    );
    if (selectedVariant && !isVariantAvailable(selectedVariant)) return;
    if (selectedVariant) setSelected(selectedVariant);
    field.onChange(nextValue);
  };

  /** Same compact select as cart line items — cart drawer template only. */
  const renderCartDrawerCompactSelect = (field) => {
    const selectedVariant =
      findVariantByFormValue(resolvedVariants, field?.value) || selected;
    const selectedUnavailable = selectedVariant
      ? !isVariantAvailable(selectedVariant)
      : false;
    const cartLayoutBody = getCustomCartDrawerLayout(popupData)?.body || {};
    const showOutOfStockBadge =
      cartLayoutBody.showVariantOutOfStockBadge !== false;
    const outOfStockBadgeText =
      String(cartLayoutBody.variantOutOfStockBadgeText || "").trim() ||
      "Out of stock";
    const outOfStockBadgeStyle = {
      color: cartLayoutBody.variantOutOfStockBadgeColor || "#64748b",
      background: cartLayoutBody.variantOutOfStockBadgeBackground || "#f8fafc",
      borderColor:
        cartLayoutBody.variantOutOfStockBadgeBorderColor || "#e2e8f0",
    };
    const options = resolvedVariants.map((variant) => ({
      id: getVariantFormValue(variant),
      label: resolveVariantDisplayTitle(variant),
      available: isVariantAvailable(variant),
    }));
    return (
      <CartDrawerVariantSelect
        options={options}
        value={
          getVariantFormValue(selectedVariant) ||
          String(field?.value || "")
        }
        disabled={isPickerDisabled}
        style={{
          fontSize: blockCSS.fontSize || "12px",
          color: selectedUnavailable
            ? "#9ca3af"
            : blockCSS.color || "#757575",
        }}
        showOutOfStockBadge={showOutOfStockBadge}
        outOfStockBadgeText={outOfStockBadgeText}
        outOfStockBadgeStyle={outOfStockBadgeStyle}
        onChange={(nextId) => applyVariantFieldChange(field, nextId)}
      />
    );
  };

  const renderNative = (field) => (
    <div style={{ height: "100%" }}>
      <select
        {...field}
        style={{
          ...blockCSS,
          height: "100%",
          width: "100%",
        }}
        disabled={isPickerDisabled}
        onChange={(e) => applyVariantFieldChange(field, e.target.value)}
      >
        {resolvedVariants.map((variant, idx) => {
          const available = isVariantAvailable(variant);
          return (
            <option
              key={idx}
              value={
                variant?.id ?? variant?.admin_graphql_api_id?.split("/")?.pop()
              }
              disabled={!available}
            >
              {withOutOfStockLabel(variant?.title, available)}
            </option>
          );
        })}
      </select>
    </div>
  );

  const renderSlider = (field) => {
    const primary = themeTokens?.primary || "var(--we-primary, #0f172a)";
    const merchantBg = blockCSS.backgroundColor || "var(--we-background, #ffffff)";
    const primarySoft =
      themeTokens?.primary
        ? `color-mix(in srgb, ${themeTokens.primary} 12%, white)`
        : "var(--ai-upsell-primary-soft, #ede9fe)";
    const borderDefault =
      blockCSS.borderColor ||
      themeTokens?.border ||
      "var(--we-border, #d1d5db)";
    const textMuted =
      blockCSS.color || themeTokens?.muted || "var(--we-text-muted, #4b5563)";
    const pillRadius =
      blockCSS.borderRadius != null && blockCSS.borderRadius !== ""
        ? typeof blockCSS.borderRadius === "number"
          ? `${blockCSS.borderRadius}px`
          : String(blockCSS.borderRadius)
        : themeTokens?.buttonRadiusPx != null
          ? `${Math.max(themeTokens.buttonRadiusPx, 10)}px`
          : "var(--ai-upsell-button-radius, 14px)";
    const borderWidth =
      typeof blockCSS.borderWidth === "number"
        ? `${blockCSS.borderWidth}px`
        : blockCSS.borderWidth || "1px";
    const borderStyle = blockCSS.borderStyle || "solid";
    const unselectedBorder = `${borderWidth} ${borderStyle} ${borderDefault}`;

    const wrapStyle = {
      ...blockCSS,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      border: "none",
      background: "transparent",
      padding: 0,
      width: "100%",
    };

    const scrollStyle = {
      display: "flex",
      gap: 12,
      width: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      scrollSnapType: "x mandatory",
      paddingBottom: 4,
    };

    return (
      <div style={wrapStyle}>
        {pickerLabel ? (
          <span
            style={{
              fontSize: isMobile ? 13 : 14,
              fontWeight: 700,
              color: themeTokens?.heading || "var(--we-text-primary, #0f172a)",
            }}
          >
            {pickerLabel}
          </span>
        ) : null}
        <div ref={tabScrollRef} style={scrollStyle}>
          {resolvedVariants.map((variant, idx) => {
            const value =
              variant?.id ?? variant?.admin_graphql_api_id?.split("/")?.pop();
            const isSelected =
              (selected?.id ?? selected?.admin_graphql_api_id?.split("/")?.pop()) ===
              value;
            const available = isVariantAvailable(variant);
            const isDisabled = isPickerDisabled || !available;
            return (
              <button
                key={idx}
                type="button"
                disabled={isDisabled}
                aria-disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    handleSelectAvailable(variant, field.onChange);
                  }
                }}
                style={{
                  whiteSpace: "nowrap",
                  borderRadius: pillRadius,
                  border: isSelected
                    ? `2px solid ${primary}`
                    : unselectedBorder,
                  backgroundColor: isSelected ? primarySoft : merchantBg,
                  color: !available
                    ? "#9ca3af"
                    : isSelected
                      ? primary
                      : textMuted,
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: isMobile ? 13 : 14,
                  padding: isMobile ? "10px 18px" : "11px 20px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: !available ? 0.55 : isPickerDisabled ? 0.72 : 1,
                  textDecoration: !available ? "line-through" : "none",
                  scrollSnapAlign: "center",
                  flexShrink: 0,
                  minWidth: 120,
                }}
              >
                {formatVariantLabel(variant)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCatalogPremiumDropdown = (field) => {
    const isHorizontalCatalogPicker = Boolean(
      containerRef.current?.closest('[data-shell-layout="cart-drawer-horizontal-v1"]')
    );
    const borderColor = blockCSS.borderColor || "#d4c4b0";
    const borderWidth =
      typeof blockCSS.borderWidth === "number"
        ? `${blockCSS.borderWidth}px`
        : blockCSS.borderWidth || "1px";
    const borderStyle = blockCSS.borderStyle || "solid";
    const shellRadius =
      blockCSS.borderRadius != null && blockCSS.borderRadius !== ""
        ? typeof blockCSS.borderRadius === "number"
          ? `${blockCSS.borderRadius}px`
          : String(blockCSS.borderRadius)
        : isProductPageUpsell
          ? "8px"
          : "8px";
    const pickerBorder = `${borderWidth} ${borderStyle} ${borderColor}`;
    const textColor = blockCSS.color || "#0f172a";
    const mutedColor = "#64748b";
    const merchantBg = blockCSS.backgroundColor || "#ffffff";

    const dropdownHeaderStyle = {
      minHeight: isHorizontalCatalogPicker
        ? isMobile
          ? 30
          : 32
        : isExtensionWidget
          ? 44
          : isMobile
            ? 36
            : 38,
      padding: isHorizontalCatalogPicker ? "0 10px" : "0 12px",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
      textAlign: "start",
      transition: "border-color 0.15s ease, opacity 0.15s ease",
      border: pickerBorder,
      borderRadius: shellRadius,
      background: merchantBg,
      backgroundColor: merchantBg,
      color: textColor,
      fontWeight: Number(blockCSS?.fontWeight) || 500,
      fontSize: blockCSS?.fontSize || (isMobile ? 11 : 12),
      lineHeight: 1.3,
      boxShadow: "none",
      width: "100%",
      boxSizing: "border-box",
      height: "100%",
    };

    const variantMenuItemCount =
      filteredVariants.length + (filteredVariants.length > 5 ? 1 : 0);
    const useMenuScroll = variantMenuItemCount > 6;

    const productPageTriggerEl =
      isProductPageUpsell && isOpen
        ? containerRef.current?.querySelector("[data-variant-picker-trigger]")
        : null;
    const resolvedProductPageMenuStyle =
      isProductPageUpsell && isOpen
        ? fixedMenuStyle ||
          buildFixedMenuStyle(productPageTriggerEl, openDirectionUp)
        : null;
    const shouldPortalProductPageMenu =
      isProductPageUpsell &&
      Boolean(resolvedProductPageMenuStyle) &&
      typeof document !== "undefined";

    const dropdownListStyle = {
      backgroundColor: merchantBg,
      border: pickerBorder,
      borderRadius: shellRadius,
      zIndex: 9999,
      width: "100%",
      maxHeight: useMenuScroll ? "220px" : "none",
      boxShadow: "0 8px 20px rgba(15, 23, 42, 0.1)",
      overflowY: useMenuScroll ? "auto" : "visible",
      padding: 4,
      boxSizing: "border-box",
      ...(resolvedProductPageMenuStyle
        ? resolvedProductPageMenuStyle
        : {
            position: "absolute",
            left: 0,
            top: openDirectionUp ? "auto" : "100%",
            bottom: openDirectionUp ? "100%" : "auto",
            marginTop: openDirectionUp ? 0 : 4,
            marginBottom: openDirectionUp ? 4 : 0,
          }),
    };

    const pickerFontSize =
      blockCSS?.fontSize || (isCatalogCompactCard ? "12px" : isMobile ? "13px" : "14px");

    const dropdownItemStyle = {
      textAlign: "left",
      borderRadius: 6,
      border: "none",
      background: "transparent",
      color: textColor,
      padding: "8px 10px",
      cursor: "pointer",
      fontSize: pickerFontSize,
      fontWeight: 500,
      transition: "background-color 0.15s ease",
    };

    const renderDropdownMenu = (listStyle) => (
      <div
        ref={dropdownRef}
        data-variant-picker-menu=""
        data-weupsell-variant-picker-portal={
          shouldPortalProductPageMenu ? "true" : undefined
        }
        style={listStyle}
      >
        {filteredVariants.length > 5 && (
          <input
            type="text"
            placeholder={section?.search?.placeholder ?? "Search variants"}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              border: pickerBorder,
              borderRadius: blockCSS.borderRadius || 6,
              padding: "7px 9px",
              marginBottom: 4,
              boxSizing: "border-box",
              fontSize: isMobile ? 11 : 12,
            }}
          />
        )}
        {filteredVariants.length > 0 ? (
          filteredVariants.map((variant, idx) => {
            const available = isVariantAvailable(variant);
            return (
              <div
                key={idx}
                role="option"
                aria-disabled={!available}
                style={{
                  ...dropdownItemStyle,
                  color: available ? dropdownItemStyle.color : "#9ca3af",
                  opacity: available ? 1 : 0.7,
                  cursor: available ? "pointer" : "not-allowed",
                  textDecoration: available ? "none" : "line-through",
                }}
                onMouseOver={(e) => {
                  if (!available) return;
                  e.currentTarget.style.backgroundColor = "#f8f6f3";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  handleSelectAvailable(variant, field.onChange);
                }}
              >
                {formatVariantLabel(variant)}
              </div>
            );
          })
        ) : (
          <div style={{ ...dropdownItemStyle, cursor: "default", textAlign: "center" }}>
            No variants found
          </div>
        )}
      </div>
    );

    const dropdownMenu =
      isOpen && !isPickerDisabled ? renderDropdownMenu(dropdownListStyle) : null;

    return (
      <div
        ref={containerRef}
        data-variant-picker-open={
          isProductPageUpsell && isOpen ? "true" : undefined
        }
        style={{
          // ...pickerShellCSS,
          border: "none",
          padding: 0,
          background: "transparent",
          width: "100%",
          position: "relative",
          display: "block",
          textAlign: "start",
          zIndex: isOpen ? 30 : undefined,
        }}
      >
        {pickerLabel ? (
          <span
            style={{
              display: "block",
              marginBottom: 6,
              fontSize: isMobile ? 12 : 13,
              fontWeight: 600,
              color: textColor,
            }}
          >
            {pickerLabel}
          </span>
        ) : null}
        <div
          data-variant-picker-trigger=""
          className="variant-picker-trigger"
          style={{
            ...dropdownHeaderStyle,
            cursor: isPickerDisabled ? "not-allowed" : "pointer",
            color:
              isPickerDisabled ||
              (selected && !isVariantAvailable(selected))
                ? mutedColor
                : textColor,
            opacity: isPickerDisabled ? 0.72 : 1,
            // Keep Design → Variant background even when the picker is disabled
            // (single / non-selectable variants). Only mute via opacity above.
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (!isPickerDisabled) {
              setIsOpen((prev) => !prev);
            }
          }}
          aria-disabled={isPickerDisabled}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
              textAlign: "start",
            }}
          >
            {selected
              ? formatVariantLabel(selected)
              : "Select variant"}
          </span>
          <PremiumChevronIcon
            open={isPickerDisabled ? false : isOpen}
            muted={
              isPickerDisabled ||
              (selected ? !isVariantAvailable(selected) : false)
            }
          />
        </div>
        {dropdownMenu &&
          (shouldPortalProductPageMenu
            ? createPortal(dropdownMenu, document.body)
            : dropdownMenu)}
      </div>
    );
  };

  const renderPremiumDropdown = (field) => {
    const borderDefault = themeTokens?.border || blockCSS.borderColor || "#d1d5db";
    const borderWidth =
      typeof blockCSS.borderWidth === "number"
        ? `${blockCSS.borderWidth}px`
        : blockCSS.borderWidth || "1px";
    const borderStyle = blockCSS.borderStyle || "solid";
    const pickerBorder = `${borderWidth} ${borderStyle} ${borderDefault}`;
    const textDefault = themeTokens?.text || blockCSS.color || "#334155";
    const headingColor = themeTokens?.heading || "#0f172a";
    const shellRadius =
      blockCSS.borderRadius != null && blockCSS.borderRadius !== ""
        ? typeof blockCSS.borderRadius === "number"
          ? `${blockCSS.borderRadius}px`
          : String(blockCSS.borderRadius)
        : themeTokens?.buttonRadiusPx != null
          ? `${themeTokens.buttonRadiusPx}px`
          : "12px";
    const merchantBg = blockCSS.backgroundColor || "#ffffff";

    const dropdownHeaderStyle = {
      ...pickerShellCSS,
      minHeight: isExtensionWidget
        ? 44
        : isCatalogCompactCard
          ? 32
          : isMobile
            ? 36
            : 40,
      padding: isCatalogCompactCard ? "0 10px" : "0 12px",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      textAlign: "start",
      transition: "background-color 0.2s ease, color 0.2s ease",
      border: pickerBorder,
      borderRadius: shellRadius,
      background: merchantBg,
      backgroundColor: merchantBg,
      color: textDefault,
      fontWeight: blockCSS.fontWeight || (isCatalogCompactCard ? 500 : 600),
      fontSize: blockCSS.fontSize || (isCatalogCompactCard ? "12px" : undefined),
      boxShadow: isOpen ? "0 8px 20px rgba(15,23,42,0.08)" : "none",
      width: "100%",
      boxSizing: "border-box",
      ...(isExtensionWidget ? { height: "100%" } : {}),
    };

    const dropdownListStyle = {
      position: "absolute",
      backgroundColor: merchantBg,
      border: pickerBorder,
      borderRadius: shellRadius,
      zIndex: 9999,
      width: "100%",
      maxHeight: "220px",
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
      left: 0,
      top: "100%",
      overflowY: "auto",
      marginTop: 6,
      padding: 6,
      boxSizing: "border-box",
    };

    const dropdownItemStyle = {
      textAlign: "left",
      borderRadius: 10,
      border: "none",
      background: "transparent",
      color: "#334155",
      padding: isCatalogCompactCard ? "7px 10px" : "9px 10px",
      cursor: "pointer",
      fontSize:
        blockCSS.fontSize ||
        (isCatalogCompactCard ? "12px" : isMobile ? 13 : 14),
      transition: "background-color 0.2s ease",
    };

    return (
      <div
        ref={containerRef}
        data-variant-picker-open={
          isProductPageUpsell && isOpen ? "true" : undefined
        }
        style={{
          // ...pickerShellCSS,
          border: "none",
          padding: 0,
          background: "transparent",
          width: "100%",
          height: isExtensionWidget ? "100%" : undefined,
          position: "relative",
          display: isExtensionWidget ? "flex" : "block",
          alignItems: isExtensionWidget ? "stretch" : undefined,
          textAlign: "start",
          zIndex: isOpen ? 30 : undefined,
        }}
      >
        {pickerLabel ? (
          <span
            style={{
              display: "block",
              marginBottom: 6,
              fontSize: isMobile ? 13 : 14,
              fontWeight: 700,
              color: headingColor,
            }}
          >
            {pickerLabel}
          </span>
        ) : null}
        <div
          data-variant-picker-trigger=""
          className="variant-picker-trigger"
          style={{
            ...dropdownHeaderStyle,
            cursor: isPickerDisabled ? "not-allowed" : "pointer",
            opacity: isPickerDisabled ? 0.72 : 1,
            color:
              isPickerDisabled ||
              (selected && !isVariantAvailable(selected))
                ? themeTokens?.muted || "#94a3b8"
                : textDefault,
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (!isPickerDisabled) {
              setIsOpen((prev) => !prev);
            }
          }}
          aria-disabled={isPickerDisabled}
        >
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
              minWidth: 0,
              textAlign: "start",
            }}
          >
            {selected ? formatVariantLabel(selected) : "Select Variant"}
          </span>
          <ArrowIcon open={isPickerDisabled ? false : isOpen} />
        </div>
        {isOpen && !isPickerDisabled && (
          <div
            ref={dropdownRef}
            style={{
              ...dropdownListStyle,
              top: openDirectionUp ? "auto" : "100%",
              bottom: openDirectionUp ? "100%" : "auto",
              marginTop: openDirectionUp ? 0 : 6,
              marginBottom: openDirectionUp ? 6 : 0,
            }}
          >
            {filteredVariants.length > 5 && (
              <input
                type="text"
                placeholder={section?.search?.placeholder ?? "Search variants"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  border: pickerBorder,
                  borderRadius: shellRadius,
                  padding: "8px 10px",
                  marginBottom: 6,
                  boxSizing: "border-box",
                  fontSize: isMobile ? 13 : 14,
                }}
              />
            )}
            {filteredVariants.length > 0 ? (
              filteredVariants.map((variant, idx) => {
                const available = isVariantAvailable(variant);
                return (
                  <div
                    key={idx}
                    role="option"
                    aria-disabled={!available}
                    style={{
                      ...dropdownItemStyle,
                      color: available ? "#334155" : "#9ca3af",
                      opacity: available ? 1 : 0.7,
                      cursor: available ? "pointer" : "not-allowed",
                      textDecoration: available ? "none" : "line-through",
                    }}
                    onMouseOver={(e) => {
                      if (!available) return;
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelectAvailable(variant, field.onChange);
                    }}
                  >
                    {formatVariantLabel(variant)}
                  </div>
                );
              })
            ) : (
              <div style={{ ...dropdownItemStyle, cursor: "default", textAlign: "center" }}>
                No variants found
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const shouldRenderPicker = isStaticCartDrawer
    ? catalogVariants.length > 1
    : resolvedVariants.length > 0 &&
      (!isCatalogCompactCard ||
        catalogVariants.length > 1 ||
        isPickerDisabled);

  if (!shouldRenderPicker) {
    return null;
  }

  const defaultVariantValue = getVariantFormValue(resolvedVariants[0]);

  const renderPickerField = (field) => {
    // Cart drawer only — match cart line-item compact select (not premium pill).
    if (isStaticCartDrawer) return renderCartDrawerCompactSelect(field);
    if (isNativeDropDown && !is_preview_mode) return renderNative(field);
    if (isSliderVariant) return renderSlider(field);
    if (
      isPremiumDropdownVariant ||
      (isProductPageUpsell && (isDropdownVariant || isPremiumDropdownVariant))
    ) {
      return renderCatalogPremiumDropdown(field);
    }
    if (isDropdownVariant) return renderPremiumDropdown(field);
    return renderSlider(field);
  };

  if (!hasFormContext) {
    const runtimeField = {
      name,
      value:
        selected?.id ??
        selected?.admin_graphql_api_id?.split("/")?.pop() ??
        defaultVariantValue ??
        "",
      onChange: () => {},
      onBlur: () => {},
      ref: () => {},
    };
    return renderPickerField(runtimeField);
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultVariantValue}
      render={({ field }) => renderPickerField(field)}
    />
  );
};

export default VariantPickerBlock;
